import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { featureFlags } from '@/utils/featureFlags';
import CompanyRedirect from '@/components/CompanyRedirect';

// Static pages (others lazy to reduce initial bundle)
// Index.tsx تم إزالته من النظام الديناميكي الموحد
const AboutUsPage = lazy(() => import('@/pages/AboutUsPage'));
const SectorsPage = lazy(() => import('@/pages/SectorsPage'));
const SectorDetailsPage = lazy(() => import('@/pages/SectorDetailsPage'));
const CompaniesPage = lazy(() => import('@/pages/CompaniesPage'));
const CompanyDetailsPage = lazy(() => import('@/pages/CompanyDetailsPage'));
const PartnersPage = lazy(() => import('@/pages/PartnersPage'));
const PartnerDetailsPage = lazy(() => import('@/pages/PartnerDetailsPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const ProjectDetailsPage = lazy(() => import('@/pages/ProjectDetailsPage'));
const MediaGalleryPage = lazy(() => import('@/pages/MediaGalleryPage'));
const ContactUsPage = lazy(() => import('@/pages/ContactUsPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsConditionsPage = lazy(() => import('@/pages/TermsConditionsPage'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Dynamic pages - simplified
import Index from '@/pages/Index';
const UnifiedDynamicPage = lazy(() => import('@/pages/UnifiedDynamicPage'));

// Blog pages (all lazy)
const BlogIndex = lazy(() => import('@/pages/BlogIndex'));
const BlogPost = lazy(() => import('@/pages/BlogPost'));
const BlogCategory = lazy(() => import('@/pages/BlogCategory'));
const BlogTag = lazy(() => import('@/pages/BlogTag'));
const BlogCategories = lazy(() => import('@/pages/BlogCategories'));
const BlogTags = lazy(() => import('@/pages/BlogTags'));
const BlogArchive = lazy(() => import('@/pages/BlogArchive'));
const BlogPreviewPage = lazy(() => import('@/pages/blog/BlogPreviewPage'));

// SEO pages
const SitemapXml = lazy(() => import('@/pages/SitemapXml'));
const BlogSitemapXml = lazy(() => import('@/pages/BlogSitemapXml'));
const RssFeed = lazy(() => import('@/pages/RssFeed'));
const RobotsTxt = lazy(() => import('@/components/seo/RobotsGenerator'));

// Admin pages (all lazy)
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const AdminLoginPage = lazy(() => import('@/pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const AdminUnifiedPagesPage = lazy(() => import('@/pages/AdminUnifiedPagesPage'));
const AdminProjectsPage = lazy(() => import('@/pages/AdminProjectsPage'));
const AdminCompaniesPage = lazy(() => import('@/pages/AdminCompaniesPage'));
const AdminPartnersPage = lazy(() => import('@/pages/AdminPartnersPage'));
const AdminMediaPage = lazy(() => import('@/pages/AdminMediaPage'));
const AdminGlobalSettingsPage = lazy(() => import('@/pages/AdminGlobalSettingsPage'));
const AdminHomePageManager = lazy(() => import('@/pages/AdminHomePageManager'));
const BlogAdminPage = lazy(() => import('@/pages/admin/BlogAdminPage'));
const BlogPostsAdminPage = lazy(() => import('@/pages/admin/BlogPostsAdminPage'));
const BlogCategoriesAdminPage = lazy(() => import('@/pages/admin/BlogCategoriesAdminPage'));
const BlogTagsAdminPage = lazy(() => import('@/pages/admin/BlogTagsAdminPage'));
const BlogSettingsAdminPage = lazy(() => import('@/pages/admin/BlogSettingsAdminPage'));
const BlogPostNewPage = lazy(() => import('@/pages/admin/BlogPostNewPage'));
const BlogPostEditPage = lazy(() => import('@/pages/admin/BlogPostEditPage'));

const AdvancedPageEditor = lazy(() => import('@/components/admin/pages/AdvancedPageEditor'));
const SimplePagesManager = lazy(() => import('@/components/admin/pages/SimplePagesManager'));
const AdminContactPage = lazy(() => import('@/pages/AdminContactPage'));
const AdminAnalyticsPage = lazy(() => import('@/pages/AdminAnalyticsPage'));
const AdminSectorsPage = lazy(() => import('@/pages/AdminSectorsPage'));
const AdminNavigationPage = lazy(() => import('@/pages/AdminNavigationPage'));
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const DebugPagesPage = lazy(() => import('@/pages/DebugPagesPage'));
const PageConfigInspector = lazy(() => import('@/components/admin/pages/PageConfigInspector'));
const AdminStatusPage = lazy(() => import('@/pages/AdminStatusPage'));
const UnifiedPageEditor = lazy(() => import('@/components/admin/pages/UnifiedPageEditor'));
const AdminServicesPage = lazy(() => import('@/pages/AdminServicesPage'));
const AdminInternalPagesManager = lazy(() => import('@/pages/AdminInternalPagesManager'));

const createAppRouter = () => {
  return createBrowserRouter([
    {
      path: '/',
      element: <Index />,
    },
    {
      path: '/admin/internal-pages',
  element: <Suspense fallback={<div />}> <AdminInternalPagesManager /> </Suspense>,
    },
    // Static internal pages (higher priority - must come before /:slug)
    {
      path: '/about',
  element: <Suspense fallback={<div />}> <AboutUsPage /> </Suspense>,
    },
    {
      path: '/sectors',
  element: <Suspense fallback={<div />}> <SectorsPage /> </Suspense>,
    },
    {
      path: '/sectors/:slug',
  element: <Suspense fallback={<div />}> <SectorDetailsPage /> </Suspense>,
    },
    {
      path: '/companies',
  element: <Suspense fallback={<div />}> <CompaniesPage /> </Suspense>,
    },
    {
      path: '/companies/:slug',
  element: <Suspense fallback={<div />}> <CompanyDetailsPage /> </Suspense>,
    },
    {
      path: '/company/:slug',
      element: <Suspense fallback={<div />}> <CompanyRedirect /> </Suspense>,
    },
    {
      path: '/partners',
  element: <Suspense fallback={<div />}> <PartnersPage /> </Suspense>,
    },
    {
      path: '/partners/:slug',
      element: <Suspense fallback={<div />}> <PartnerDetailsPage /> </Suspense>,
    },
    {
      path: '/projects',
  element: <Suspense fallback={<div />}> <ProjectsPage /> </Suspense>,
    },
    {
      path: '/projects/:slug',
  element: <Suspense fallback={<div />}> <ProjectDetailsPage /> </Suspense>,
    },
    {
      path: '/media-gallery',
  element: <Suspense fallback={<div />}> <MediaGalleryPage /> </Suspense>,
    },
    {
      path: '/contact',
  element: <Suspense fallback={<div />}> <ContactUsPage /> </Suspense>,
    },
    {
      path: '/privacy',
  element: <Suspense fallback={<div />}> <PrivacyPolicyPage /> </Suspense>,
    },
    {
      path: '/terms',
  element: <Suspense fallback={<div />}> <TermsConditionsPage /> </Suspense>,
    },
    // Blog routes (must come before /:slug)
    {
      path: '/blog',
      element: <Suspense fallback={<div />}> <BlogIndex /> </Suspense>,
    },
    {
      path: '/blog/categories',
      element: <Suspense fallback={<div />}> <BlogCategories /> </Suspense>,
    },
    {
      path: '/blog/tags', 
      element: <Suspense fallback={<div />}> <BlogTags /> </Suspense>,
    },
    {
      path: '/blog/archive',
      element: <Suspense fallback={<div />}> <BlogArchive /> </Suspense>,
    },
    {
      path: '/blog/category/:slug',
      element: <Suspense fallback={<div />}> <BlogCategory /> </Suspense>,
    },
    {
      path: '/blog/tag/:slug',
      element: <Suspense fallback={<div />}> <BlogTag /> </Suspense>,
    },
    {
      path: '/blog/:slug',
      element: <Suspense fallback={<div />}> <BlogPost /> </Suspense>,
    },
    // SEO routes
    {
      path: '/sitemap.xml',
      element: <Suspense fallback={<div />}> <SitemapXml /> </Suspense>,
    },
    {
      path: '/sitemap-blog.xml',
      element: <Suspense fallback={<div />}> <BlogSitemapXml /> </Suspense>,
    },
    {
      path: '/feed.xml',
      element: <Suspense fallback={<div />}> <RssFeed /> </Suspense>,
    },
    {
      path: '/robots.txt',
      element: <Suspense fallback={<div />}> <RobotsTxt /> </Suspense>,
    },
    // Admin routes (must come before /:slug)
    {
      path: '/admin',
  element: <Suspense fallback={<div />}> <AdminPage /> </Suspense>,
    },
    {
      path: '/admin/login',
  element: <Suspense fallback={<div />}> <AdminLoginPage /> </Suspense>,
    },
    {
      path: '/admin/dashboard',
  element: <Suspense fallback={<div />}> <AdminDashboardPage /> </Suspense>,
    },
    {
      path: '/admin/simple-pages',
  element: <Suspense fallback={<div />}> <SimplePagesManager /> </Suspense>,
    },
    {
      path: '/admin/pages',
  element: <Suspense fallback={<div />}> <AdminUnifiedPagesPage /> </Suspense>,
    },
    {
      path: '/admin/pages/editor/:id',
  element: <Suspense fallback={<div />}> <AdvancedPageEditor /> </Suspense>,
    },
    {
      path: '/admin/projects',
  element: <Suspense fallback={<div />}> <AdminProjectsPage /> </Suspense>,
    },
    {
      path: '/admin/companies',
  element: <Suspense fallback={<div />}> <AdminCompaniesPage /> </Suspense>,
    },
    {
      path: '/admin/partners',
  element: <Suspense fallback={<div />}> <AdminPartnersPage /> </Suspense>,
    },
    {
      path: '/admin/media',
  element: <Suspense fallback={<div />}> <AdminMediaPage /> </Suspense>,
    },
    {
      path: '/admin/settings',
  element: <Suspense fallback={<div />}> <AdminGlobalSettingsPage /> </Suspense>,
    },
    {
      path: '/admin/home',
  element: <Suspense fallback={<div />}> <AdminHomePageManager /> </Suspense>,
    },
    {
      path: '/admin/contact',
  element: <Suspense fallback={<div />}> <AdminContactPage /> </Suspense>,
    },
    {
      path: '/admin/analytics',
  element: <Suspense fallback={<div />}> <AdminAnalyticsPage /> </Suspense>,
    },
    {
      path: '/admin/sectors',
  element: <Suspense fallback={<div />}> <AdminSectorsPage /> </Suspense>,
    },
    {
      path: '/admin/navigation',
  element: <Suspense fallback={<div />}> <AdminNavigationPage /> </Suspense>,
    },
    {
      path: '/admin/services',
      element: <Suspense fallback={<div />}> <AdminServicesPage /> </Suspense>,
    },
    {
      path: '/admin/blog',
      element: <Suspense fallback={<div />}> <BlogAdminPage /> </Suspense>,
    },
    {
      path: '/admin/blog/posts',
      element: <Suspense fallback={<div />}> <BlogPostsAdminPage /> </Suspense>,
    },
    {
      path: '/admin/blog/categories',
      element: <Suspense fallback={<div />}> <BlogCategoriesAdminPage /> </Suspense>,
    },
    {
      path: '/admin/blog/tags',
      element: <Suspense fallback={<div />}> <BlogTagsAdminPage /> </Suspense>,
    },
    {
      path: '/admin/blog/settings',
      element: <Suspense fallback={<div />}> <BlogSettingsAdminPage /> </Suspense>,
    },
    {
      path: '/admin/blog/posts/new',
      element: <Suspense fallback={<div />}> <BlogPostNewPage /> </Suspense>,
    },
    {
      path: '/admin/blog/posts/:id/edit',
      element: <Suspense fallback={<div />}> <BlogPostEditPage /> </Suspense>,
    },
    {
      path: '/admin/users',
  element: <Suspense fallback={<div />}> <AdminUsersPage /> </Suspense>,
    },
    {
      path: '/admin/status',
  element: <Suspense fallback={<div />}> <AdminStatusPage /> </Suspense>,
    },
    // Dynamic Editor (عزل المحرر الديناميكي)
    {
      path: '/admin/dynamic-editor',
  element: <Suspense fallback={<div />}> <Index /> </Suspense>,
    },
    {
      path: '/admin/dynamic-editor/:slug',
  element: <Suspense fallback={<div />}> {(() => {
        console.info('[ROUTE] Dynamic Page Editor Route: /admin/dynamic-editor/:slug');
        return <UnifiedDynamicPage />;
  })()} </Suspense>,
    },
    // Dynamic page route - /p/:slug (stable and unified)
    {
      path: '/p/:slug',
  element: <Suspense fallback={<div />}> {(() => {
        console.info('[ROUTE] Dynamic Page Route:', { 
          path: '/p/:slug', 
          dynamicEnabled: featureFlags.ff_dynamic_pages_runtime,
          component: 'UnifiedDynamicPage'
        });
        return <UnifiedDynamicPage />;
  })()} </Suspense>,
    },
    {
      path: '/admin/pages/:slug/inspect',
      element: <Suspense fallback={<div />}> <PageConfigInspector slug={':slug'} /> </Suspense>,
    },
    {
      path: '/admin/pages/:slug/edit',
      element: <Suspense fallback={<div />}> <UnifiedPageEditor slug={':slug'} /> </Suspense>,
    },
    // Debug page (development only)
    {
      path: '/__debug/pages',
  element: <Suspense fallback={<div />}> {process.env.NODE_ENV === 'development' ? <DebugPagesPage /> : <NotFound />} </Suspense>,
    },
    // 404 fallback
    {
      path: '*',
  element: <Suspense fallback={<div />}> <NotFound /> </Suspense>,
    },
  ]);
};

export const AppRouter: React.FC = () => {
  const router = createAppRouter();
  return <RouterProvider router={router} />;
};

export default AppRouter;
